'use client'

import { useEffect, useRef } from 'react'
import { ArrowRight, ExternalLink } from 'lucide-react'
import '../styles/shipped-evidence.css'

// Showcase cards (origin -> connector -> adaptation) and the sticky
// top-to-bottom stack they scroll in, shared by the service pages
// (ShippedEvidenceSection) and the homepage (home-v2/ShowcaseStackV2).
// Kept free of the snapshot JSON import so the homepage bundle does not pull
// in every page's showcase data.

function isImageUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim())
}

function normalizeTags(tags) {
  if (Array.isArray(tags)) return tags.filter(Boolean)
  if (typeof tags === 'string') {
    try {
      const parsed = JSON.parse(tags)
      if (Array.isArray(parsed)) return parsed.filter(Boolean)
    } catch {
      return tags.split(',').map((t) => t.trim()).filter(Boolean)
    }
  }
  return []
}

// Renders the brand logo inside a fixed-height frame so the FULL logo is always
// visible (contain, never cropped), regardless of the brand's aspect ratio.
// Returns null when there is no icon, so the name simply leads instead.
function OriginLogo({ icon, name }) {
  if (!icon) return null
  if (isImageUrl(icon)) {
    return (
      <div className="se-origin-logo-frame">
        <img
          className="se-origin-logo"
          src={icon}
          alt={name ? `${name} logo` : 'logo'}
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.currentTarget.parentElement.style.display = 'none'
          }}
          onLoad={(e) => {
            if (!e.currentTarget.naturalWidth) e.currentTarget.parentElement.style.display = 'none'
          }}
        />
      </div>
    )
  }
  return (
    <div className="se-origin-logo-frame">
      <span className="se-origin-logo-emoji" aria-hidden="true">{icon}</span>
    </div>
  )
}

export function ShowcasePairContent({ item, index }) {
  const tags = normalizeTags(item.adaptation_tags)
  const originHref = item.origin_url
    ? (/^https?:\/\//i.test(item.origin_url) ? item.origin_url : `https://${item.origin_url}`)
    : null

  return (
    <>
      <div className="se-pair-origin">
        <span className="se-kicker">Consumer Origin</span>
        <OriginLogo icon={item.origin_icon} name={item.origin_name} />
        <strong className="se-origin-name">{item.origin_name}</strong>
        {originHref ? (
          <a className="se-origin-url" href={originHref} target="_blank" rel="noreferrer">
            {item.origin_url.replace(/^https?:\/\//i, '')} <ExternalLink size={12} aria-hidden="true" />
          </a>
        ) : null}
        {item.origin_description ? <p className="se-origin-desc">{item.origin_description}</p> : null}
        {item.origin_tagline ? <p className="se-origin-tagline">{item.origin_tagline}</p> : null}
      </div>

      <div className="se-pair-connector" aria-hidden="true">
        <span className="se-arrow"><ArrowRight size={18} /></span>
        <span className="se-index">{String(index + 1).padStart(2, '0')}</span>
      </div>

      <div className="se-pair-adaptation">
        <span className="se-adapt-badge">{item.adaptation_badge || 'Enterprise Adaptation'}</span>
        <h3 className="se-adapt-name">{item.adaptation_name}</h3>
        {item.adaptation_description ? <p className="se-adapt-desc">{item.adaptation_description}</p> : null}
        {tags.length > 0 ? (
          <div className="se-tags">
            {tags.map((tag) => (
              <span key={tag} className="se-tag">{tag}</span>
            ))}
          </div>
        ) : null}
      </div>
    </>
  )
}

// The sticky stack: each card recedes (scales down a little) as the next one
// slides over it. Progress per card runs from 0 the moment it sticks to 1
// once the next card has stuck on top of it. Raw progress moves in lockstep
// with the finger/wheel, which reads as mechanical, so the scale eases toward
// it a little each frame instead of snapping (the feel the framer-motion
// spring used to give).
//
// Opacity is deliberately NOT part of that recede -- a translucent card lets
// its own text show through the opaque card stacked on top of it, which
// reads as double-exposed, ghosted text rather than depth. The card has to
// stay fully solid for the next one to actually cover it; scale is the only
// cue that it is receding. It is written to the `scale` property, so it never
// fights the card's own transform or its entrance (`translate`).
const RECEDE_TO = 0.93
const EASE_PER_FRAME = 0.18

function useStackRecede(listRef, items) {
  useEffect(() => {
    const list = listRef.current
    if (!list || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined

    const cards = Array.from(list.children)
    const current = cards.map(() => 0)
    let ranges = []
    let frame = 0

    // Positions as if nothing were sticky (a stuck card's own rect follows
    // the viewport), relative to the list: every earlier card's height and
    // margin, less each card's sticky `top`. Relative, so content loading
    // above the list (which moves it) needs no re-measure; the list's own
    // position is read once per frame instead.
    const measure = () => {
      let y = 0
      const tops = cards.map((card) => {
        const top = y
        y += card.offsetHeight + (parseFloat(getComputedStyle(card).marginBottom) || 0)
        return top
      })
      const stuckAt = cards.map((card, i) => tops[i] - (parseFloat(getComputedStyle(card).top) || 0))
      ranges = cards.map((card, i) => {
        const end = i + 1 < cards.length ? stuckAt[i + 1] : stuckAt[i] + card.offsetHeight
        return { from: stuckAt[i], span: Math.max(1, end - stuckAt[i]) }
      })
    }

    const tick = () => {
      frame = 0
      if (!ranges.length) return
      // How far the page has scrolled past the list's top. Read before any
      // writes, so it never forces a layout.
      const scrolled = -list.getBoundingClientRect().top
      let settling = false
      cards.forEach((card, i) => {
        const { from, span } = ranges[i]
        const target = Math.min(1, Math.max(0, (scrolled - from) / span))
        const next = current[i] + (target - current[i]) * EASE_PER_FRAME
        current[i] = Math.abs(target - next) < 0.001 ? target : next
        if (current[i] !== target) settling = true
        card.style.scale = String(1 - (1 - RECEDE_TO) * current[i])
      })
      if (settling) frame = requestAnimationFrame(tick)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(tick)
    }
    const remeasure = () => {
      measure()
      schedule()
    }

    // No measuring here: reading sizes during hydration would force a layout
    // of the whole page. ResizeObserver reports once right after the next
    // layout (and again on any size change), when the reads are free.
    window.addEventListener('scroll', schedule, { passive: true })
    const resize = new ResizeObserver(remeasure)
    resize.observe(list)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      resize.disconnect()
      cards.forEach((card) => {
        card.style.scale = ''
      })
    }
  }, [listRef, items])
}

export function ShowcaseStack({ items }) {
  const listRef = useRef(null)
  useStackRecede(listRef, items)
  if (!items?.length) return null
  return (
    <div className="se-pairs" ref={listRef}>
      {items.map((item, index) => (
        <article
          key={item.id}
          className="se-pair"
          data-rv="0.3"
          style={{ '--i': index, '--rv-y': '46px', '--rv-dur': '.75s' }}
        >
          <ShowcasePairContent item={item} index={index} />
        </article>
      ))}
    </div>
  )
}
